import { useEffect, useRef, useState } from 'react';
import type { StationSnapshot } from './types';
import { api } from './api';
import { ls } from './storage';
import { wsBus } from './wsBus';
import { primeBreath } from './useBreath';

const DUCK = 0.22; // music gain while the DJ speaks
const RAMP_MS = 400;

// YouTube thumbnails for the OS Now-Playing card. The OS fetches these, so cross-origin is fine;
// sizes are the real pixel dimensions. iOS shows the first usable one, Android/desktop the largest.
const artworkFor = (videoId?: string | null): MediaImage[] =>
  videoId
    ? [
        { src: `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`, sizes: '320x180', type: 'image/jpeg' },
        { src: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`, sizes: '480x360', type: 'image/jpeg' },
        { src: `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`, sizes: '640x480', type: 'image/jpeg' },
      ]
    : [];

export function useAudio(snap: StationSnapshot, onOfflineEnded?: () => Promise<boolean>) {
  const ref = useRef<HTMLAudioElement | null>(null);
  if (!ref.current) ref.current = new Audio();
  const audio = ref.current;

  const speechRef = useRef<HTMLAudioElement | null>(null);
  if (!speechRef.current) speechRef.current = new Audio();
  const speech = speechRef.current;

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blocked, setBlocked] = useState(false); // iOS blocked audible playback (needs a tap)
  const [buffering, setBuffering] = useState(false);
  // this device's own radio switch: the station is global, but only devices whose speaker is
  // on actually make sound. Play turns it on, pause turns it off, and it survives reloads.
  // Defaults OFF: a device may only make sound after play has been pressed on it at least once.
  const [speaker, setSpeakerState] = useState(() => ls.get('claudio.speaker') === 'on');
  const speakerRef = useRef(speaker);
  const setSpeaker = (on: boolean) => {
    speakerRef.current = on;
    setSpeakerState(on);
    ls.set('claudio.speaker', on ? 'on' : 'off');
  };
  const [volume, setVolume] = useState(() => {
    const v = Number(ls.get('claudio.volume'));
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  });

  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const duckRef = useRef(1);
  const rafRef = useRef<number | undefined>(undefined);
  const spokenUid = useRef<string | null>(null);

  const applyVolume = () => {
    audio.volume = Math.max(0, Math.min(1, volumeRef.current * duckRef.current));
  };

  const rampDuck = (target: number) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = duckRef.current;
    const t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / RAMP_MS);
      duckRef.current = start + (target - start) * k;
      applyVolume();
      if (k < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  // hard stop on unmount so an audio element can never be orphaned and keep playing
  useEffect(() => {
    return () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      speech.pause();
      speech.removeAttribute('src');
    };
  }, [audio, speech]);

  // "这首完了"必须送达: 一次上报在行车网络下常丢, 电台会卡在放完的歌上——重试到
  // 送达或电台已前进为止; uid 让服务器忽略迟到的重复上报
  const endedTimer = useRef<number | undefined>(undefined);
  const reportEnded = () => {
    const uid = snapRef.current.nowPlaying?.track.uid;
    if (!uid) return;
    if (endedTimer.current) clearTimeout(endedTimer.current);
    let tries = 0;
    const attempt = () => {
      if (snapRef.current.nowPlaying?.track.uid !== uid) return; // station moved on
      api.control('ended', undefined, uid).catch(async () => {
        // 够不着服务器 → 先本地切到本机存好的下一首, 事件落盘等回网同步
        if (tries === 0 && onOfflineEnded && (await onOfflineEnded())) return;
        if (++tries < 10) endedTimer.current = window.setTimeout(attempt, 3000);
      });
    };
    attempt();
  };

  // media element wiring (once)
  useEffect(() => {
    speech.volume = 1;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      syncToStation();
    };
    const onEnded = () => reportEnded();
    const onPlaying = () => {
      primeBreath(audio);
      setBuffering(false);
    };
    const onWaiting = () => setBuffering(true);
    const onCanPlay = () => setBuffering(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
    };
  }, [audio, speech]);

  useEffect(() => {
    applyVolume();
    ls.set('claudio.volume', String(volume));
  }, [volume]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    wsBus.setListening(speaker);
  }, [speaker]);

  // self-heal: element finished but the station still says playing → the ended report was
  // lost — re-report instead of waiting for the user to notice the silence
  useEffect(() => {
    const t = window.setInterval(() => {
      if (!speakerRef.current) return;
      const s = snapRef.current;
      if (s.status === 'playing' && s.nowPlaying?.track.file && audio.ended) reportEnded();
    }, 5000);
    return () => window.clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const file = snap.nowPlaying?.track.file ?? null;
  const uid = snap.nowPlaying?.track.uid ?? null;
  const ttsFile = snap.nowPlaying?.track.ttsFile ?? null;

  // late joiners tune to the station's position, not the top of the song. Clock skew and
  // fresh starts both land under the 3s threshold, so those keep currentTime untouched.
  const syncToStation = () => {
    const s = snapRef.current;
    if (s.status !== 'playing' || !s.nowPlaying) return;
    const off = (Date.now() - s.nowPlaying.startedAt) / 1000;
    const d = audio.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    if (off > 3 && off < d - 2 && Math.abs(off - audio.currentTime) > 2) {
      audio.currentTime = off;
      setCurrentTime(off);
    }
  };

  // load the music source when the track changes
  useEffect(() => {
    if (file) {
      if (!audio.src.endsWith(file)) audio.src = file;
    } else {
      audio.removeAttribute('src');
      audio.load();
      setCurrentTime(0);
      setDuration(0);
      setBuffering(false);
    }
    // reset duck for a fresh track
    duckRef.current = 1;
    applyVolume();
  }, [file, audio]); // eslint-disable-line react-hooks/exhaustive-deps

  // follow play/pause from the server — but only make sound if this device's speaker is on
  useEffect(() => {
    if (file) {
      if (snap.status === 'playing' && speaker) {
        if (audio.ended) {
          reportEnded(); // 已放完却还被要求播放 = 上报丢了; 补报, 千万别从头重放
        } else {
          syncToStation();
          audio
            .play()
            .then(() => setBlocked(false))
            .catch(() => setBlocked(true)); // iOS blocks non-gesture playback → surface a tap prompt
        }
      } else {
        audio.pause();
      }
    }
    if (snap.status !== 'playing' || !speaker) speech.pause();
    else if (speech.src && !speech.ended && spokenUid.current === uid) {
      speech.play().catch(() => {});
    }
  }, [snap.status, file, uid, speaker, audio, speech]); // eslint-disable-line react-hooks/exhaustive-deps

  // speak the DJ banter over the track intro, ducking the music
  useEffect(() => {
    if (!uid || !ttsFile || snap.status !== 'playing') return;
    if (spokenUid.current === uid) return;
    if (!speaker) {
      spokenUid.current = uid; // a silent follower joining later shouldn't blurt a stale intro
      return;
    }
    // only speak near the start; if TTS arrived late, skip to avoid talking mid-song
    if (audio.currentTime > 4) {
      spokenUid.current = uid;
      return;
    }
    spokenUid.current = uid;
    speech.src = ttsFile;
    rampDuck(DUCK);
    speech.play().catch(() => rampDuck(1));
    const restore = () => rampDuck(1);
    speech.addEventListener('ended', restore, { once: true });
    speech.addEventListener('error', restore, { once: true });
  }, [uid, ttsFile, snap.status, speaker]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- OS media controls (lock screen / Bluetooth / headset / Control Center) ---------------
  // setPositionState THROWS on non-finite/<=0 duration, position out of range, or rate 0 — guard all.
  const updatePositionState = () => {
    const ms = navigator.mediaSession;
    if (!ms || !ms.setPositionState) return;
    const d = audio.duration;
    if (!Number.isFinite(d) || d <= 0) {
      try {
        ms.setPositionState();
      } catch {
        /* ignore */
      }
      return;
    }
    const position = Math.min(Math.max(audio.currentTime, 0), d);
    try {
      ms.setPositionState({ duration: d, position, playbackRate: audio.playbackRate || 1 });
    } catch {
      /* ignore */
    }
  };

  // register transport handlers once. Deliberately NOT registering seekbackward/seekforward:
  // on iOS those REPLACE the previous/next-track buttons on the lock screen.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const set = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        ms.setActionHandler(action, handler);
      } catch {
        /* unsupported action on this platform */
      }
    };
    set('play', () => {
      setSpeaker(true);
      if (audio.ended) {
        reportEnded();
        return;
      }
      primeBreath(audio);
      syncToStation();
      audio.play().then(() => setBlocked(false)).catch(() => {});
      api.control('play').catch(() => {});
    });
    set('pause', () => {
      setSpeaker(false);
      audio.pause();
      api.control('pause').catch(() => {});
    });
    set('previoustrack', () => api.control('prev').catch(() => {}));
    set('nexttrack', () => api.control('next').catch(() => {}));
    set('seekto', (d) => {
      if (d.seekTime == null) return;
      audio.currentTime = d.seekTime;
      setCurrentTime(d.seekTime);
      updatePositionState();
    });
    return () => {
      (['play', 'pause', 'previoustrack', 'nexttrack', 'seekto'] as MediaSessionAction[]).forEach((a) =>
        set(a, null),
      );
    };
  }, [audio]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the Now-Playing metadata, play/pause glyph, and scrubber in sync with the station
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const np = snap.nowPlaying?.track;
    if (np) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: np.title,
        artist: np.artist, // note: setting artist hides album on iOS
        album: 'Claudio',
        artwork: artworkFor(np.videoId),
      });
    }
    // when autoplay is blocked the audio isn't actually audible — show 'paused' so the OS
    // glyph matches reality until the user taps to start playback
    navigator.mediaSession.playbackState =
      snap.status === 'playing' && !blocked && speaker
        ? 'playing'
        : snap.status === 'paused' || !speaker
          ? 'paused'
          : 'none';
    updatePositionState();
    // videoId is included because it arrives AFTER the track is set (resolve is async) under the
    // same uid — without it the lock-screen artwork would stay empty for the whole track
  }, [uid, snap.nowPlaying?.track.videoId, snap.status, duration, blocked, speaker]); // eslint-disable-line react-hooks/exhaustive-deps

  const seek = (t: number) => {
    audio.currentTime = t;
    setCurrentTime(t);
    updatePositionState();
  };

  // tiny silent WAV — used to "unlock" the audio element from inside a user gesture
  // when there's no real stream loaded yet (idle queue). After this primes the element,
  // a later audio.play() with the real stream src succeeds without needing another gesture.
  const SILENT_WAV =
    'data:audio/wav;base64,UklGRhwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
  const primedRef = useRef(false);

  // called from a real tap to satisfy the browser's user-gesture requirement
  const resume = () => {
    if (!speakerRef.current) return; // silent follower — controls shouldn't start sound here
    if (audio.ended && snapRef.current.status === 'playing') {
      reportEnded();
      return;
    }
    syncToStation();
    // if the real stream isn't loaded yet, prime once with a silent placeholder so the
    // browser marks this <audio> as user-activated. The next render (with the actual src)
    // will then play without being blocked, even though it runs in an async effect.
    if ((!audio.src || audio.src.endsWith(SILENT_WAV.slice(-12))) && !primedRef.current) {
      try {
        audio.src = SILENT_WAV;
        audio.play().catch(() => {});
        primedRef.current = true;
      } catch {
        /* ignore */
      }
      return;
    }
    audio
      .play()
      .then(() => setBlocked(false))
      .catch((e) => {
        console.warn('[claudio] audio.play rejected:', e?.name, e?.message);
        try {
          audio.load();
          audio
            .play()
            .then(() => setBlocked(false))
            .catch((e2) => console.warn('[claudio] retry rejected:', e2?.name, e2?.message));
        } catch (e2) {
          console.warn('[claudio] reload/play threw:', (e2 as Error).message);
        }
      });
    if (speech.src) speech.play().then(() => speech.pause()).catch(() => {});
  };

  return { currentTime, duration, volume, setVolume, seek, blocked, buffering, resume, speaker, setSpeaker, el: audio };
}

export function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
